import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { PlayIcon } from 'lucide-react';

import { ApiError, api } from '@/lib/api';
import { useContent, useT } from '@/lib/i18n';
import { InfoHint } from '@/components/info-hint';
import { PageHeader } from '@/components/page-header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useCannotBecause } from '@/lib/permissions';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

/**
 * Start a run (build/05 §F1).
 *
 * Picks a persona, a module and a candidate reference, calls `POST
 * /assessments`, and navigates to the harness. `startOrResume` means a repeat
 * for the same (candidate, module) returns the OPEN instance rather than drawing
 * a second one — so this screen doubles as "resume", and a 200 instead of a 201
 * is not an error to report.
 *
 * `candidateRef` is an opaque identifier and the only thing about a person that
 * this platform stores. There is deliberately no name field.
 */
export function StartRun() {
  const navigate = useNavigate();
  const t = useT();
  const content = useContent();
  const [candidateRef, setCandidateRef] = useState('');
  const [personaId, setPersonaId] = useState('');
  const [moduleId, setModuleId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const personas = useQuery({ queryKey: ['personas'], queryFn: api.personas });
  const modules = useQuery({ queryKey: ['modules'], queryFn: api.modules });

  const start = useMutation({
    mutationFn: () => api.startAssessment({ candidateRef: candidateRef.trim(), moduleId, personaId }),
    onSuccess: (instance) => navigate(`/run/${instance.assessmentId}`),
    onError: (err) => setError(err instanceof ApiError ? err.message : t('startRun.failed')),
  });

  const activeModules = (modules.data?.items ?? []).filter((m) => m.status === 'active');
  const ready = candidateRef.trim().length > 0 && personaId && moduleId;
  /**
   * Generating an instance is a write, and this screen is in the nav for every
   * role — an analyst can reach it, fill it in completely, and only then be
   * refused. So the refusal is stated up front, as an alert rather than only a
   * tooltip: the form has three fields above the button and a disabled control
   * at the bottom of one is easy to read as "something above is incomplete".
   */
  const cannot = useCannotBecause('assessments:run');

  return (
    <div className="mx-auto max-w-2xl">
      <PageHeader
        title={t('nav.take')}
        description={t('startRun.description')}
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('startRun.newRun')}</CardTitle>
          <CardDescription>{t('startRun.resumeHint')}</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4">
          <div className="grid gap-2">
            <Label htmlFor="candidateRef">
              {t('startRun.candidateRef')}
              <InfoHint>{t('startRun.candidateRef.detail')}</InfoHint>
            </Label>
            <Input
              id="candidateRef"
              dir="ltr"
              value={candidateRef}
              onChange={(e) => setCandidateRef(e.target.value)}
              placeholder="cand_qa_0001"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="persona">{t('startRun.persona')}</Label>
            <Select value={personaId} onValueChange={setPersonaId}>
              <SelectTrigger id="persona">
                <SelectValue placeholder={t('startRun.selectPersona')} />
              </SelectTrigger>
              <SelectContent>
                {(personas.data?.items ?? []).map((p) => (
                  <SelectItem key={p.personaId} value={p.personaId}>
                    {content(p.name)} · {p.segment}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="module">
              {t('questions.column.module')}
              <InfoHint>{t('startRun.module.detail')}</InfoHint>
            </Label>
            <Select value={moduleId} onValueChange={setModuleId}>
              <SelectTrigger id="module">
                <SelectValue placeholder={t('startRun.selectModule')} />
              </SelectTrigger>
              <SelectContent>
                {activeModules.map((m) => (
                  <SelectItem key={m.moduleId} value={m.moduleId}>
                    {content(m.name)} · {m.modelType}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {error && (
            <Alert variant="destructive" role="alert">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {cannot && (
            <Alert role="status">
              <AlertDescription>{cannot}</AlertDescription>
            </Alert>
          )}

          <Button
            onClick={() => start.mutate()}
            disabled={!ready || start.isPending || cannot !== null}
            title={cannot ?? undefined}
          >
            <PlayIcon className="size-4 rtl:rotate-180" />
            {start.isPending ? t('startRun.generating') : t('startRun.start')}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
